/* eslint-disable @typescript-eslint/no-unused-expressions */
import { GridColDef } from "@mui/x-data-grid-pro";
import {
  EditIcon,
  GKButton,
  GKClientDataGrid,
  Grid2,
  IconButton,
  SendNotificationIcon,
  Show,
  StatusField,
} from "components";
import DotStatusField from "components/DotStatusField";
import { useFetchQuery } from "hooks/useQueries";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "store/store";
import { UserRoleTypes } from "utils/common";
import {
  extraColumnsField,
  handleGetTotalDays,
  matchDate,
} from "utils/helpers";
import { moneyCommaSeparator } from "utils/MathFunction";
import useDocumentTitle from "utils/useDocumentTitle";
import RecommendationTimelineModal from "./RecommendationTimelineModal";
import SendRecommendationNotification from "./SendRecommendationNotification";

const EquityBasketStockRecommedation = () => {
  useDocumentTitle("Basket Assets");

  const userData = useAppSelector((state) => state?.user?.userData);

  const { id } = useParams();

  const navigate = useNavigate();

  const [selectedRecommendation, setSelectedRecommendation] = useState(null);

  const [showRecommendationHistory, setShowRecommendationHistory] =
    useState(false);

  const [
    showRecommendationNotificationModal,
    setShowRecommendationNotificationModal,
  ] = useState(false);

  const [allStocks, setAllStocks] = useState([]);
  /** ========================================================================================================= */
  const { isFetching } = useFetchQuery({
    key: ["STOKE_UNIVERSE"],
    route: `model_portfolio/${id}/stock_universe/?is_recommendation=true`,
    success: (response: any) => {
      setAllStocks(response?.data?.allocItem);
    },
  });

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Rec. Date", "dateAdded", 150, 1),
      valueFormatter: (params) => matchDate(params.value),
    },
    {
      ...extraColumnsField("Stocks", "companyName", 150, 1),
    },
    {
      ...extraColumnsField("Rec. Type", "recommendationType", 80, 1),
      renderCell: (params) => {
        return (
          <StatusField
            statusString={params?.row?.recommendationType || "N/A"}
          />
        );
      },
    },
    {
      ...extraColumnsField("Target Price", "targetPrice", 100, 1),
      valueFormatter: (params) => moneyCommaSeparator(params?.value || 0),
      type: "number",
    },
    {
      ...extraColumnsField("Stop Loss", "stopLoss", 100, 1),
      valueFormatter: (params) => moneyCommaSeparator(params?.value || 0),
      type: "number",
    },
    {
      ...extraColumnsField("Upside Pending", "upsidePending", 100, 1),
      valueFormatter: (params) => moneyCommaSeparator(params?.value || 0),
      type: "number",
    },
    {
      ...extraColumnsField(
        "Days Since Rec.",
        "daysSinceRecommendation",
        100,
        1,
      ),
      type: "number",
      valueGetter: (d) => handleGetTotalDays(d.row.dateAdded),
    },
    {
      ...extraColumnsField("Status", "status", 100, 1),
      renderCell: (params) => {
        return <DotStatusField statusString={params?.row?.status || "N/A"} />;
      },
    },
    {
      ...extraColumnsField("Action", "action", 110, 1),
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        return (
          <Grid2 container justifyContent={"flex-end"}>
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowRecommendationNotificationModal(true);
                setSelectedRecommendation(params.row.id);
              }}
            >
              <SendNotificationIcon width={18} height={18} />
            </IconButton>
            <IconButton
              className="outline"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(
                  `/app/equity-basket/${id}/edit-recommendation/${params.row.id}/`,
                );
              }}
            >
              <EditIcon height={18} width={18} />
            </IconButton>
          </Grid2>
        );
      },
    },
  ];

  return (
    <>
      <Grid2 container>
        <Grid2 size={12}>
          <GKClientDataGrid
            name="stockUniverse"
            maxHeight={`calc(100vh - 340px)`}
            onRowClick={(params) => {
              setShowRecommendationHistory(true);
              setSelectedRecommendation(params?.row?.id);
            }}
            headerComponent={
              userData?.userRole === UserRoleTypes.advisor && (
                <GKButton
                  variant="contained"
                  onClick={() => {
                    navigate(`/app/equity-basket/${id}/add-recommendation`);
                  }}
                >
                  Add Recommendation
                </GKButton>
              )
            }
            pinnedColumns={{
              left: ["companyName"],
            }}
            loading={isFetching}
            rows={allStocks || []}
            columns={columns.filter((dataItem) =>
              userData?.userRole === UserRoleTypes.advisor
                ? dataItem
                : dataItem.field !== "action",
            )}
          />
        </Grid2>
      </Grid2>

      <Show.When isTrue={showRecommendationHistory}>
        <RecommendationTimelineModal
          open={showRecommendationHistory}
          selectedRecommendation={selectedRecommendation}
          setOpen={setShowRecommendationHistory}
        />
      </Show.When>

      <Show.When isTrue={showRecommendationNotificationModal}>
        <SendRecommendationNotification
          open={showRecommendationNotificationModal}
          selectedRecommendation={selectedRecommendation}
          setOpen={setShowRecommendationNotificationModal}
        />
      </Show.When>
    </>
  );
};

export default EquityBasketStockRecommedation;
